import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from "react";
import type { Document } from "@/services/documentService";
import { useDomainEventInvalidation } from "@/hooks/useDomainEventInvalidation";
import { useProjectDocumentIndexingEvents } from "@/hooks/useProjectDocumentIndexingEvents";
import { useProjectDocumentStore, selectProjectDocuments } from "@/stores/useProjectDocumentStore";

/**
 * Fetches and returns project-scoped documents (with lineageEdges from API).
 */
export function useProjectDocuments(projectId) {
  const $ = _c(35);
  let t0;
  if ($[0] !== projectId) {
    t0 = s => selectProjectDocuments(s, projectId);
    $[0] = projectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const documents = useProjectDocumentStore(t0);
  let t1;
  if ($[2] !== projectId) {
    t1 = s_0 => projectId ? s_0.byProjectId[projectId]?.loading ?? false : false;
    $[2] = projectId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const loading = useProjectDocumentStore(t1);
  let t2;
  if ($[4] !== projectId) {
    t2 = s_1 => projectId ? s_1.byProjectId[projectId]?.error ?? null : null;
    $[4] = projectId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const error = useProjectDocumentStore(t2);
  const fetchProjectDocuments = useProjectDocumentStore(_temp);
  const invalidateProject = useProjectDocumentStore(_temp2);
  const upsertProjectDocumentSummary = useProjectDocumentStore(_temp3);
  let t3;
  if ($[6] !== invalidateProject || $[7] !== projectId) {
    t3 = projectId ? () => invalidateProject(projectId) : undefined;
    $[6] = invalidateProject;
    $[7] = projectId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = {
      onMalformedEvent: t3
    };
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useProjectDocumentIndexingEvents(projectId, t4);
  const t5 = projectId ?? undefined;
  let t6;
  if ($[11] !== t5) {
    t6 = {
      projectId: t5
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["document.changed"];
    t8 = ["document"];
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  let t9;
  if ($[15] !== invalidateProject || $[16] !== projectId || $[17] !== upsertProjectDocumentSummary) {
    t9 = event => {
      if (!projectId) {
        return;
      }
      const {
        documentId,
        documentName,
        documentSize,
        documentType,
        indexingStatus
      } = event.payload ?? {};
      if (documentId && documentName && documentSize !== undefined && documentType && indexingStatus) {
        upsertProjectDocumentSummary(projectId, {
          id: documentId,
          documentName,
          documentSize,
          documentType,
          indexingStatus
        });
      } else {
        invalidateProject(projectId);
      }
    };
    $[15] = invalidateProject;
    $[16] = projectId;
    $[17] = upsertProjectDocumentSummary;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = Boolean(projectId);
  let t11;
  if ($[19] !== t10 || $[20] !== t6 || $[21] !== t9) {
    t11 = {
      scope: t6,
      eventTypes: t7,
      entityTypesAffected: t8,
      onInvalidate: t9,
      enabled: t10
    };
    $[19] = t10;
    $[20] = t6;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useDomainEventInvalidation(t11);
  let t12;
  let t13;
  if ($[23] !== fetchProjectDocuments || $[24] !== projectId) {
    t12 = () => {
      if (!projectId) {
        return;
      }
      fetchProjectDocuments(projectId);
    };
    t13 = [projectId, fetchProjectDocuments];
    $[23] = fetchProjectDocuments;
    $[24] = projectId;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  if ($[27] !== invalidateProject || $[28] !== projectId) {
    t14 = () => {
      if (projectId) {
        invalidateProject(projectId);
      }
    };
    $[27] = invalidateProject;
    $[28] = projectId;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const reload = t14;
  let t15;
  if ($[30] !== documents || $[31] !== error || $[32] !== loading || $[33] !== reload) {
    t15 = {
      documents,
      loading,
      error,
      reload
    };
    $[30] = documents;
    $[31] = error;
    $[32] = loading;
    $[33] = reload;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15 satisfies {
    documents: Document[];
    loading: boolean;
    error: string | null;
    reload: () => void;
  };
}
function _temp3(s_4) {
  return s_4.upsertProjectDocumentSummary;
}
function _temp2(s_3) {
  return s_3.invalidateProject;
}
function _temp(s_2) {
  return s_2.fetchProjectDocuments;
}